import { useContext } from "react";
import type { ComponentProps } from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { tv } from "tailwind-variants";

const inputOtpVariants = tv({
  slots: {
    root: "disabled:cursor-not-allowed",
    container: "flex items-center gap-2 has-disabled:opacity-50 p-1",
    group: "flex items-center gap-2 w-full",
    slot: "flex-1 text-3xl h-[70px] data-[active=true]:border-ring data-[active=true]:ring-ring/50 data-[active=true]:aria-invalid:ring-destructive/20 dark:data-[active=true]:aria-invalid:ring-destructive/40 aria-invalid:border-destructive data-[active=true]:aria-invalid:border-destructive bg-neutral-25 border-gray-20 relative flex items-center justify-center border rounded-md shadow-xs transition-all outline-none data-[active=true]:z-10 data-[active=true]:ring-[3px]",
    caretWrapper:
      "pointer-events-none absolute inset-0 flex items-center justify-center",
    caret: "h-6 w-0.5 animate-caret-blink bg-black duration-1000",
  },
});

const { caret, caretWrapper, container, group, root, slot } =
  inputOtpVariants();

export const Root = ({
  className,
  containerClassName,
  ...props
}: ComponentProps<typeof OTPInput> & {
  containerClassName?: string;
}) => {
  return (
    <OTPInput
      className={root({ className })}
      containerClassName={container({ className: containerClassName })}
      data-slot="input-otp"
      {...props}
    />
  );
};

export const Group = ({ className, ...props }: ComponentProps<"div">) => {
  return (
    <div
      className={group({ className })}
      data-slot="input-otp-group"
      {...props}
    />
  );
};

export const Slot = ({
  className,
  index,
  ...props
}: ComponentProps<"div"> & {
  index: number;
}) => {
  const inputOTPContext = useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};

  return (
    <div
      className={slot({ className })}
      data-active={isActive}
      data-slot="input-otp-slot"
      {...props}
    >
      {char}
      {hasFakeCaret ? <Caret /> : null}
    </div>
  );
};

export const Caret = () => {
  return (
    <div className={caretWrapper()}>
      <div className={caret()} />
    </div>
  );
};

export const Separator = (props: ComponentProps<"div">) => {
  return (
    <div data-slot="input-otp-separator" role="separator" {...props}>
      -
    </div>
  );
};

export const InputOTP = {
  Root,
  Group,
  Slot,
  Separator,
};
